import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs/lib/anime.es.js";
import { Settings } from "../../../settings";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  FEATURES_CONFIG,
  WIN_TYPE_THRESHOLDS,
  ANIMATED_WIN_TYPES,
} from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES } from "../../../fontStyles";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import {
  setWinAnimationsPlaying,
  setSymbolPopupBlocked,
  setWinAnimationCounterFinished,
  refreshGameData,
  updateDisplayedBalance,
} from "../../../State/actions";
import store from "../../../State/Store/store";
import { formatToCustomLocale } from "../../../utilities";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import { HowlCache } from "../../../State/Slices/soundsSlice";
import { handleShouldSyncBalanceOnWinClose } from "../../../service";

// Win animation timing — each spine tier animation lasts exactly 3 seconds
const SPINE_ANIMATION_DURATION = 3000;

// ============================================================================
// WIN ANIMATION CONFIGURATION
// Fine-tune spine scale, position offset, and text sizes per device/orientation
// ============================================================================

// Device multipliers for spine scale and vertical offset
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  scale: 0.8,
  offset: 0.7,
  buttonFontScale: 1.4,
  buttonYOffsetMultiplier: 0.85,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  scale: 0.75,
  offset: 0,
  buttonFontScale: 1.35,
  buttonYOffsetMultiplier: 0.65,
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  scale: 0.95,
  offset: 0.65,
  buttonFontScale: 1.5,
  buttonYOffsetMultiplier: 1.5,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  scale: 0.9,
  offset: 0,
  buttonFontScale: 1.2,
  buttonYOffsetMultiplier: 1.5,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  scale: 0.75,
  offset: 0.1,
  buttonFontScale: 1.2,
  buttonYOffsetMultiplier: 1.0,
};

// Counter text configuration
const COUNTER_CONFIG = {
  FONT_SIZE: 128, // Base font size for counter digits
};

/**
 * WinAnimationController - Manages progressive win celebration animations
 *
 * Features:
 * - Progressive win sequence: BIG_WIN → MEGA_WIN → SUPER_WIN → EPIC_WIN → MAX_WIN
 * - Each win follows: IN animation → IDLE loop → OUT animation
 * - Integrated counter attached to spine's "value" bone (auto-synced position/scale/rotation)
 * - Counter value animates with sound effects
 * - User can skip counter animation or close entire sequence
 * - Auto-close after counter finishes (configurable delay)
 *
 * @class
 */
class WinAnimationController {
  /**
   * Creates a new WinAnimationController instance
   *
   * @param {PIXI.Container} container - Parent container for spine animations
   * @param {Object} dimensionsContext - Device dimensions and context
   * @param {Object} overlayRef - Reference to background overlay
   * @param {Function} dispatch - Redux dispatch function
   * @param {number} symbolWidth - Width of game symbols for scaling
   * @param {number} symbolHeight - Height of game symbols for scaling
   * @param {Object} clickIndicatorRef - Reference to click indicator UI
   * @param {Object} buttonRef - Reference to continue button UI
   * @param {Function} t - Translation function from i18next
   */
  constructor(
    container,
    dimensionsContext,
    overlayRef,
    dispatch,
    symbolWidth,
    symbolHeight,
    clickIndicatorRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.overlayRef = overlayRef;
    this.dispatch = dispatch;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.clickIndicatorRef = clickIndicatorRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.winSpine = null;
    this.isVisible = false;
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.initialized = false;

    // Counter attached to spine bone
    this.counterContainer = null;
    this.counterText = null;
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;

    // Timeouts and cleanup
    this.currentTimeouts = [];
    this.emergencyRecoveryTimeout = null;
    this.autoCloseTimeout = null;
    this.skipProtectionTimeout = null;
    this.debounceTimeout = null;

    // Animation state
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.lastWinAmount = null; // Prevent restart for same win

    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // User interaction state
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false;
    this.isFinishing = false;
    this.counterJumpedToFinal = false;
    this.counterDetached = false; // Track whether counter is detached from spine slot
    this.detachedScaleX = 1; // Scale factor when counter is detached
    this.detachedScaleY = 1;

    // Background music state tracking
    this.backgroundMusicWasPlaying = false;
    this.backgroundMusicType = null; // Track which background music was playing (background_music or free_spin_background_music)

    // Internal caches for performance optimization
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._animationProgressionCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    // Initialize click indicator to hidden state
    this.hideClickIndicatorInstant();

    this.initializeWinSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.winSpine || !this.dimensions) return;

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Get device/orientation-specific multipliers
    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.winSpine.scale.set(scale);

    // Apply device/orientation-specific offset multiplier
    const baseOffset = this.symbolHeight || 0;

    // Position relative to centered parent container (0,0 is screen center)
    this.winSpine.x = 0;
    this.winSpine.y = -baseOffset * deviceOffsetMultiplier;

    // Update detached counter position to match new spine transform
    this.syncDetachedCounter();
  }

  /**
   * Get device-specific scale and offset multipliers for spine animations
   * Returns both scale and offset values from configuration constants
   * Cached for performance - invalidated when device/orientation changes
   */
  getDeviceMultipliers() {
    const { device, orientation } = this.dimensionsContext;

    // Check cache validity
    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    // Cache result
    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multipliers;
  }

  /**
   * Show click indicator with fade-in animation
   */
  showClickIndicator() {
    if (this.clickIndicatorRef && this.clickIndicatorRef.current) {
      anime({
        targets: this.clickIndicatorRef.current,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }
  }

  /**
   * Hide click indicator with fade-out animation
   */
  hideClickIndicator() {
    if (this.clickIndicatorRef && this.clickIndicatorRef.current) {
      anime({
        targets: this.clickIndicatorRef.current,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }
  }

  /**
   * Hide click indicator instantly (for cleanup)
   */
  hideClickIndicatorInstant() {
    if (this.clickIndicatorRef && this.clickIndicatorRef.current) {
      this.clickIndicatorRef.current.alpha = 0.001;
    }
  }

  /**
   * Show continue button with fade-in animation
   * Spine does NOT animate txt_slot_tap, so we handle visibility manually
   */
  showButton() {
    if (!this.buttonContainer) return;

    anime({
      targets: this.buttonContainer,
      alpha: 1,
      duration: 300,
      easing: "easeOutQuad",
    });

    // Keep old React button hidden
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    if (!this.buttonContainer) return;
    // Instant hide — button has served its purpose, let spine OUT play clean
    this.buttonContainer.alpha = 0.001;

    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    // Get translated button text based on current device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Update text content
    this.buttonText.text = buttonText;

    // Recalculate font size proportionally to spine scale + device multiplier
    const fontSize = this._computeButtonFontSize();
    this.buttonText.style.fontSize = fontSize;
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensionsContext, symbolWidth, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightsChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed - this prevents unnecessary updates during animation start
    if (!dimensionsChanged && !symbolHeightsChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions; // Update the extracted dimensions object
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Invalidate caches when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text and style if device/orientation changed
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }
    this.updateButtonStyle();
    this.updateButtonPosition();

    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    // Similar to FreeSpinIntroDialogController which maintains positioning at all times
    if (
      this.winSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightsChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }

    // Update counter display if dimensions changed (font size needs adjustment based on spine scale)
    if (dimensionsChanged || symbolHeightsChanged) {
      if (this.counterContainer && this.currentCounterValue !== undefined) {
        // Font size is now based on spine scale, so update when spine is rescaled
        this.updateCounterDisplay(this.currentCounterValue);
      }
    }
  }

  /**
   * Clear all tracked timeouts and timers
   *
   * Clears animation timeouts, auto-close timers, skip protection timers,
   * and emergency recovery timers. Prevents memory leaks and ensures
   * clean state when animations are interrupted.
   */
  clearAllTimeouts() {
    for (const t of this.currentTimeouts) {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    }
    this.currentTimeouts = [];

    // Clear skip protection timeout
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }

    // Clear emergency recovery timeout
    if (this.emergencyRecoveryTimeout) {
      this.emergencyRecoveryTimeout.clear();
      this.emergencyRecoveryTimeout = null;
    }

    // Clear auto-close timeout
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }

    // Clear debounce timeout
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
  }

  /**
   * Clear all resources (just timeouts now, anime fades handle themselves)
   */
  clearAllResources() {
    this.clearAllTimeouts();
  }

  /**
   * Initialize the win celebration spine
   */
  initializeWinSpine() {
    try {
      this.winSpine = this.createWinSpine();

      if (!this.winSpine) {
        return;
      }

      // Simple validation
      if (!this.winSpine.skeleton || !this.winSpine.state) {
        return;
      }

      // Add to container first
      this.container.addChild(this.winSpine);

      // Set initial properties
      this.winSpine.alpha = 0.001; // Hidden initially (use alpha not visible)

      // Apply initial position and scale (only if dimensions are ready)
      // If dimensions aren't ready yet, updateDimensions will position it when they become available
      if (this.symbolHeight && this.symbolHeight > 0 && this.dimensions) {
        this.applyScaleAndCenter();
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      // Initialize spine structure to compile shaders during loading
      this.initializeSpineStructure();

      // Force-upload all win_celebration atlas page textures to GPU
      SpineAtlasUploader.upload("win_celebration");

      // Initialize counter attached to spine bone
      this.initializeCounter();

      // Initialize button attached to spine bone
      this.initializeButton();
    } catch (error) {
      // Silent fail - spine initialization failed
    }
  }

  /**
   * Create win spine from loaded assets
   */
  createWinSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const winSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!winSpine || !winSpine.skeleton || !winSpine.state) {
        return null;
      }

      if (!winSpine.skeleton.data || !winSpine.skeleton.data.animations) {
        return null;
      }

      return winSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.winSpine || !this.winSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      // Just pick the first available animation (usually big_win_in)
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.winSpine.state.setAnimation(0, animations[0], false);
        this.winSpine.state.update(0.016); // Update one frame
        this.winSpine.skeleton.updateWorldTransform(); // Populate hierarchy
      }

      // Keep at alpha = 0.001 so PixiJS continues rendering it
      // This keeps GPU buffers hot after skeleton is populated
      this.winSpine.alpha = 0.001;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.winSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.winSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize counter and attach to spine slot "txt_slot_value"
   *
   * The counter is attached to a spine slot, so Spine automatically handles
   * position, rotation, and scale updates. No manual transform updates needed.
   * The counter only manages its own text content and visibility.
   */
  initializeCounter() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Check if slot exists in spine
    const counterSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
    if (!counterSlot) {
      return;
    }

    // Create counter container and attach to slot
    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "win_counter_attached_to_slot";
    this.counterContainer.alpha = 0.001;

    // Create single PIXI.Text using JSON font style
    const style = FONT_STYLES.winCounter;
    this.counterText = new PIXI.Text(
      "0",
      new PIXI.TextStyle({
        ...style,
        fontSize: COUNTER_CONFIG.FONT_SIZE,
      }),
    );
    this.counterText.anchor.set(0.5);
    this.counterText.roundPixels = true;
    this.counterContainer.addChild(this.counterText);

    // Attach to slot - Spine auto-updates position/rotation/scale
    this.winSpine.addSlotObject("txt_slot_value", this.counterContainer);

    // Initialize with zero
    this.updateCounterDisplay(0);
  }

  /**
   * Update counter display with new value
   * Uses character-by-character spacing to prevent text shifting when value changes
   * Slot handles all positioning/scaling/rotation
   *
   * @param {number} value - The value to display
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer || !this.counterText) return;

    const formattedText = formatToCustomLocale(Math.floor(value));
    this.counterText.text = formattedText;

    // Auto-scale counter to fit within screen width
    const maxWidth = (this.dimensions?.width || 800) * 0.85;
    const totalWidth = this.counterText.width;

    if (this.counterDetached) {
      // When detached, detachedScale already maps local→screen space.
      // Convert maxWidth to local space so comparison is consistent.
      const localMaxWidth =
        this.detachedScaleX > 0 ? maxWidth / this.detachedScaleX : maxWidth;
      if (totalWidth > localMaxWidth && localMaxWidth > 0) {
        const scale = localMaxWidth / totalWidth;
        this.counterContainer.scale.set(
          scale * this.detachedScaleX,
          scale * this.detachedScaleY,
        );
      } else {
        this.counterContainer.scale.set(
          this.detachedScaleX,
          this.detachedScaleY,
        );
      }
    } else {
      if (totalWidth > maxWidth && maxWidth > 0) {
        const scale = maxWidth / totalWidth;
        this.counterContainer.scale.set(scale);
      } else {
        this.counterContainer.scale.set(1);
      }
    }
  }

  /**
   * Show counter (visibility only, spine controls position/scale/rotation)
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
  }

  /**
   * Hide counter (visibility only, spine controls position/scale/rotation)
   */
  hideCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 0.001;
  }

  /**
   * Detach counter from spine slot — counter stays at fixed position,
   * unaffected by bone transforms (IN/OUT scale animations).
   * Used between tier transitions to keep counter visible.
   */
  detachCounterFromSlot() {
    if (!this.counterContainer || this.counterDetached || !this.winSpine) {
      return;
    }

    // Find the bone that drives the txt_slot_value slot
    // The slot is bound to "position" bone (child of "txt_value"), NOT "txt_value" itself
    const bone = this.winSpine.skeleton.findBone("position");
    if (!bone) {
      return;
    }

    // Ensure bone world transforms are current
    this.winSpine.skeleton.updateWorldTransform(2);

    // Calculate position: bone world coords → screen → parent-local
    const globalPos = this.winSpine.toGlobal({
      x: bone.worldX,
      y: bone.worldY,
    });
    const localPos = this.container.toLocal(globalPos);

    // Capture spine pixi scale and bone scale at detach time
    // These are frozen so tier OUT/IN animations don't corrupt the counter
    const spineScaleX = Math.abs(this.winSpine.scale.x);
    const spineScaleY = Math.abs(this.winSpine.scale.y);
    const boneScaleX = Math.sqrt(bone.a * bone.a + bone.b * bone.b);
    const boneScaleY = Math.sqrt(bone.c * bone.c + bone.d * bone.d);

    // Save for sync on resize
    this._detachSpineScaleX = spineScaleX;
    this._detachSpineScaleY = spineScaleY;
    this._detachBoneScaleX = boneScaleX;
    this._detachBoneScaleY = boneScaleY;

    // Use spine's API to properly remove from slot (clears internal slotsObject map)
    this.winSpine.removeSlotObject("txt_slot_value", this.counterContainer);

    // Null out the slot's original attachment so spine doesn't render any baked-in
    // visual (e.g. region placeholder) during intermediate IN animations
    const slot = this.winSpine.skeleton.findSlot("txt_slot_value");
    if (slot) {
      slot.setAttachment(null);
    }

    // Add to parent container (completely outside spine's hierarchy)
    this.container.addChild(this.counterContainer);

    // Apply position and scale
    this.counterContainer.x = localPos.x;
    this.counterContainer.y = localPos.y;
    this.detachedScaleX = spineScaleX * boneScaleX;
    this.detachedScaleY = spineScaleY * boneScaleY;
    this.counterContainer.scale.set(this.detachedScaleX, this.detachedScaleY);
    this.counterContainer.rotation = 0;

    this.counterDetached = true;
  }

  /**
   * Sync detached counter position/scale with spine's current transform.
   * Called from applyScaleAndCenter on resize to keep counter aligned.
   *
   * IMPORTANT: Uses frozen bone scale from detach time, NOT the current
   * animated bone transforms. During tier OUT/IN animations, the bone
   * scales to 0 — reading it here would corrupt the counter size.
   * Only the spine's pixi scale (stable device-based value) is re-read.
   */
  syncDetachedCounter() {
    if (!this.counterDetached || !this.counterContainer || !this.winSpine)
      return;

    // Recalculate position from spine's current pixi transform
    const bone = this.winSpine.skeleton.findBone("position");
    if (!bone) return;

    this.winSpine.skeleton.updateWorldTransform(2);

    const globalPos = this.winSpine.toGlobal({
      x: bone.worldX,
      y: bone.worldY,
    });
    const localPos = this.container.toLocal(globalPos);

    this.counterContainer.x = localPos.x;
    this.counterContainer.y = localPos.y;

    // Scale: use FROZEN bone scale from detach time, only adjust for spine pixi scale change
    const currentSpineScaleX = Math.abs(this.winSpine.scale.x);
    const currentSpineScaleY = Math.abs(this.winSpine.scale.y);

    this.detachedScaleX = currentSpineScaleX * this._detachBoneScaleX;
    this.detachedScaleY = currentSpineScaleY * this._detachBoneScaleY;

    // Apply scale directly and re-render counter text with current value
    this.counterContainer.scale.set(this.detachedScaleX, this.detachedScaleY);
    this.updateCounterDisplay(this.currentCounterValue);
  }

  /**
   * Reattach counter to spine slot — counter follows bone transforms again.
   * Used before closing OUT animation so counter scales out with the bone.
   */
  reattachCounterToSlot() {
    if (!this.counterContainer || !this.counterDetached || !this.winSpine)
      return;

    // Remove from parent container
    if (this.counterContainer.parent) {
      this.counterContainer.parent.removeChild(this.counterContainer);
    }

    // Re-attach to spine slot — bone transforms will drive position/scale again
    this.winSpine.addSlotObject("txt_slot_value", this.counterContainer);

    this.counterDetached = false;
    this.detachedScaleX = 1;
    this.detachedScaleY = 1;
  }

  /**
   * Initialize button and attach to spine slot "txt_slot_tap"
   *
   * The button is attached to a spine slot, so Spine automatically handles
   * position, rotation, and scale updates. No manual transform updates needed.
   * If slot doesn't exist, button remains invisible.
   */
  initializeButton() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Create container as direct child of overlay (NOT attached to spine slot)
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "win_animation_continue_overlay";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001;

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Single-layer text using JSON font style (dropShadow handled natively)
    const style = FONT_STYLES.winCelebration;
    const fontSize = this._computeButtonFontSize();

    this.buttonText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...style,
        fontSize,
      }),
    );
    this.buttonText.roundPixels = true;
    this.buttonText.anchor.set(0.5);

    // Add text to container
    this.buttonContainer.addChild(this.buttonText);

    // Add to overlay container directly (not to spine slot)
    this.container.addChild(this.buttonContainer);

    // Position based on device config
    this.updateButtonPosition();
  }

  /**
   * Update button position based on current dimensions and device config.
   * Button is positioned independently using symbolHeight * multiplier.
   * Called on init and on every resize.
   */
  updateButtonPosition() {
    if (!this.buttonContainer || !this.dimensions) return;

    const { buttonYOffsetMultiplier } = this.getDeviceMultipliers();
    const multiplier = buttonYOffsetMultiplier ?? 1.0;

    // Container is centered at screen center, so y=0 = center
    // Positive multiplier = push below center
    const buttonY = this.dimensions.height / 2 - this.symbolHeight * multiplier;

    this.buttonContainer.x = 0;
    this.buttonContainer.y = buttonY;
  }

  /**
   * Update button text style (font size) when device/orientation changes.
   * Recalculates fontSize proportionally to spine scale + buttonFontScale.
   */
  updateButtonStyle() {
    if (!this.buttonText) return;

    const style = FONT_STYLES.winCelebration;
    const fontSize = this._computeButtonFontSize();

    // Update text style from JSON font style
    this.buttonText.style = new PIXI.TextStyle({
      ...style,
      fontSize,
    });
  }

  /**
   * Compute button font size proportionally to the spine scale.
   * Uses the same symbolHeight/nativeSize ratio the spine uses,
   * then applies the per-device buttonFontScale multiplier.
   * This keeps the button text scaling in lockstep with the spine.
   */
  _computeButtonFontSize() {
    const baseFontSize = 32;
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const spineRatio = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    const { scale: deviceScaleMultiplier, buttonFontScale } =
      this.getDeviceMultipliers();
    return Math.round(
      baseFontSize *
        spineRatio *
        deviceScaleMultiplier *
        (buttonFontScale || 1.0),
    );
  }

  /**
   * Animate counter value from one number to another
   *
   * @param {number} fromValue - Starting value
   * @param {number} toValue - Target value
   * @param {number} duration - Animation duration in ms
   * @param {Function} onComplete - Callback when animation completes
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    // Start counter sound
    soundManager.play(SOUND_NAME.counter);

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: "easeOutQuad",
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;

        // Stop counter sound
        soundManager.stop(SOUND_NAME.counter);

        // Call completion callback
        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Instantly set counter to specific value (skips animation)
   *
   * @param {number} value - Value to display
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Reset counter to zero
   */
  resetCounter() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    soundManager.stop(SOUND_NAME.counter);
  }

  /**
   * Check if counter value is currently animating
   *
   * @returns {boolean} True if counter is animating
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Get current counter value
   *
   * @returns {number} Current counter value
   */
  getCurrentCounterValue() {
    return this.currentCounterValue || 0;
  }

  /**
   * Calculate which win animations should play based on win multiplier
   *
   * Determines progressive win sequence based on win amount relative to bet:
   * - 20x+ bet = BIG_WIN animation
   * - 40x+ bet = BIG_WIN + MEGA_WIN animations
   * - 60x+ bet = BIG_WIN + MEGA_WIN + SUPER_MEGA_WIN animations
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for multiplier calculation
   * @returns {string[]} Array of win types in sequence (e.g., ['big_win', 'mega_win'])
   */
  calculateAnimationSequence(winAmount, betAmount) {
    if (!winAmount || !betAmount || winAmount <= 0 || betAmount <= 0) {
      return [];
    }

    // Read pre-computed winType from Redux state (set by RGS adapter)
    const state = store.getState();
    const finalWinType = state.game.currentRound?.winType || "";

    // If it's not an animated win type, return empty array
    if (!finalWinType || !ANIMATED_WIN_TYPES.includes(finalWinType)) {
      return [];
    }

    // Build sequence dynamically from provider's animated win types
    // Order win types by their thresholds (ascending)
    const winTypeOrder = ANIMATED_WIN_TYPES.map((winType) => ({
      type: winType,
      threshold: WIN_TYPE_THRESHOLDS[winType] || 0,
    }))
      .sort((a, b) => a.threshold - b.threshold)
      .map((item) => item.type);

    const sequence = [];
    const multiplier = winAmount / betAmount;

    // Add all win types that the multiplier qualifies for
    for (const winType of winTypeOrder) {
      // Skip max_win if feature is disabled
      if (winType === "max_win" && !FEATURES_CONFIG.MAX_WIN.ENABLED) continue;

      if (this.shouldPlayWinType(winType, multiplier)) {
        sequence.push(winType);
      }

      // Stop when we reach the final win type
      if (winType === finalWinType) {
        break;
      }
    }

    return sequence;
  }

  /**
   * Check if a win type should be played based on multiplier
   */
  shouldPlayWinType(winType, multiplier) {
    const threshold = WIN_TYPE_THRESHOLDS[winType] || 0;
    return threshold > 0 && multiplier >= threshold;
  }

  /**
   * Play banner sound for win type (plays for each animation)
   * Uses the win sound directly (no banner versions exist)
   */
  playBannerSound = (winType) => {
    // max_win uses max_win_in as its banner sound (no generic max_win sound exists)
    const soundKey = winType === "max_win" ? "max_win_in" : winType;
    if (!this.dispatch || !SOUND_NAME[soundKey]) return;

    try {
      soundManager.play(SOUND_NAME[soundKey]);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade in win music on loop for entire sequence (replaces individual win sounds)
   */
  playMainWinSound = () => {
    try {
      // Delay win_music fade-in to let pauseBackgroundMusic() fade-out finish (500ms)
      const fadeDelay = new TickerTimeout(() => {
        const winMusic = HowlCache[SOUND_NAME.win_music];
        if (winMusic) {
          winMusic.loop(true); // Ensure it loops
          soundManager.fadeIn(SOUND_NAME.win_music, 500);
        }
      }, 500);
      this.currentTimeouts.push(fadeDelay);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade out background music if it's playing (smooth transition to win music)
   */
  pauseBackgroundMusic() {
    try {
      // Check both background music options and fade out whichever is playing
      const backgroundMusic = HowlCache[SOUND_NAME.background_music];
      const freeSpinBackgroundMusic =
        HowlCache[SOUND_NAME.free_spin_background_music];

      if (backgroundMusic && backgroundMusic.playing()) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = SOUND_NAME.background_music;
        soundManager.fadeOut(SOUND_NAME.background_music, 500);
      } else if (freeSpinBackgroundMusic && freeSpinBackgroundMusic.playing()) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = SOUND_NAME.free_spin_background_music;
        soundManager.fadeOut(SOUND_NAME.free_spin_background_music, 500);
      } else {
        this.backgroundMusicWasPlaying = false;
        this.backgroundMusicType = null;
      }
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Fade in background music if it was playing before (smooth transition from win music)
   */
  resumeBackgroundMusic() {
    try {
      if (this.backgroundMusicWasPlaying && this.backgroundMusicType) {
        soundManager.fadeIn(this.backgroundMusicType, 500);
      }
      this.backgroundMusicWasPlaying = false; // Reset flag
      this.backgroundMusicType = null;
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Stop banner sounds (win sounds specific to each animation)
   * Uses the win sound directly (no banner versions exist)
   */
  stopBannerSounds() {
    if (!this.dispatch) return;

    try {
      // Stop win sounds used for animations (no banner versions exist)
      const bannerSounds = [
        SOUND_NAME.big_win,
        SOUND_NAME.mega_win,
        SOUND_NAME.super_win,
        SOUND_NAME.epic_win,
        SOUND_NAME.max_win_in,
      ];

      bannerSounds.forEach((soundName) => {
        if (soundName) {
          soundManager.stop(soundName);
        }
      });
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Stop all win sounds (banner + main)
   */
  stopAllWinSounds() {
    this.stopBannerSounds();
    this.stopWinSounds();
    // Also stop win_music_end and max_win sounds if playing (for emergency cleanup)
    try {
      if (SOUND_NAME.win_music_end) {
        soundManager.stop(SOUND_NAME.win_music_end);
      }
      if (SOUND_NAME.max_win_music) {
        soundManager.stop(SOUND_NAME.max_win_music);
      }
      if (SOUND_NAME.max_win_out) {
        soundManager.stop(SOUND_NAME.max_win_out);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Fade out main win music (replaces stopping individual win sounds)
   */
  stopWinSounds() {
    if (!this.dispatch) return;

    try {
      // Fade out win_music instead of stopping it abruptly
      if (SOUND_NAME.win_music) {
        soundManager.fadeOut(SOUND_NAME.win_music, 500);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Start the progressive win animation sequence
   *
   * Determines which win animations to play based on win amount and bet size,
   * then plays them in sequence (BIG → SUPER → MEGA → EPIC).
   * Each animation follows: IN → IDLE → OUT pattern.
   * Counter animation runs in sync with spine animations.
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for win type threshold calculation
   * @returns {Promise} Resolves when animation sequence is complete
   */
  startSequence(winAmount, betAmount) {
    return new Promise((resolve, reject) => {
      // Prevent retriggering during finishing phase and duplicate wins
      if (
        this.isPlaying ||
        this.isFinishing ||
        winAmount === this.lastWinAmount ||
        !this.winSpine ||
        !this.winSpine.state
      ) {
        resolve();
        return;
      }

      // Calculate which animations to play
      this.animationQueue = this.calculateAnimationSequence(
        winAmount,
        betAmount,
      );

      if (this.animationQueue.length === 0) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.currentAnimationIndex = 0; // Start at 0 for first animation
      this.finalWinType = this.animationQueue[this.animationQueue.length - 1];
      this.totalWinAmount = winAmount;
      this.betAmount = betAmount;
      this.lastWinAmount = winAmount; // Track this win amount to prevent restart
      this.counterJumpedToFinal = false; // Reset counter jump flag

      // Reset user interaction guards at the beginning of a sequence
      this.hasHandledSkipClick = false;
      this.hasHandledCloseClick = false;
      this.isHandlingUserClick = false;
      this.isWaitingForClick = false; // Reset waiting state for new sequence
      this.isClosing = false; // Reset closing state for new sequence

      // SKIP PROTECTION: Block skipping for the first second
      this.canSkipAnimation = false;
      if (this.skipProtectionTimeout) {
        this.skipProtectionTimeout.clear();
      }
      this.skipProtectionTimeout = new TickerTimeout(() => {
        // Safety check: only enable skip if animation is still playing and not finished
        if (
          this.isPlaying &&
          !(this.hasHandledSkipClick && this.hasHandledCloseClick)
        ) {
          this.canSkipAnimation = true;
          this.showClickIndicator();
          this.showButton();
        }
      }, 1000); // 1 second protection

      // Enable scaling calculations when animation becomes active
      this.shouldBeActive = true;

      // Pause background music before starting win animation
      this.pauseBackgroundMusic();

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing (spine is always warmed up and positioned)
      if (
        this.winSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Show and start animation - positioning is already correct since spine is always maintained
      this.showAndStartAnimation(resolve, reject);
    });
  }

  /**
   * Show spine, counter, overlay and start animation (extracted for reuse)
   */
  showAndStartAnimation(resolve, reject) {
    // Fade in spine smoothly (synchronized with counter/overlay)
    if (this.winSpine) {
      anime({
        targets: this.winSpine,
        alpha: [0.001, 1],
        duration: 350, // Match counter fade-in duration
        easing: "easeOutQuad",
      });
    }

    this.isVisible = true;
    this.currentAnimationPhase = "spine-animation";
    this.phaseStartTime = performance.now();
    this.phaseProgress = 0;

    // Initialize counter
    this.resetCounter();
    this.showCounter();
    this.startBoneUpdateLoop();

    // Show background overlay (synchronized)
    this.overlayRef.current?.fadeInBackground();

    // Start animation after positioning is confirmed
    this.startFirstAnimation().then(resolve).catch(reject);
  }

  /**
   * DEPRECATED: No longer needed with slot-based attachment
   * Spine automatically updates slot object transforms
   */
  startBoneUpdateLoop() {
    // No-op: Spine handles transform updates automatically via slot attachment
  }

  /**
   * DEPRECATED: No longer needed with slot-based attachment
   */
  stopBoneUpdateLoop() {
    // No-op: Spine handles transform updates automatically via slot attachment
  }

  /**
   * Play the next animation in the queue
   * Intermediate animations jump straight to IDLE (no IN/OUT) so the counter stays visible.
   * Only the first animation plays IN, only the last plays OUT on close.
   */
  playNextAnimation() {
    return new Promise((resolve, reject) => {
      if (this.currentAnimationIndex >= this.animationQueue.length) {
        resolve();
        return;
      }

      const winType = this.animationQueue[this.currentAnimationIndex];
      const isLastAnimation =
        this.currentAnimationIndex === this.animationQueue.length - 1;

      // Play banner sound for this animation
      this.playBannerSound(winType);

      // Detach counter from slot BEFORE reset — keeps counter visible at scale 1
      // while spine IN animation plays (bone scale 0→1 won't affect detached counter)
      this.detachCounterFromSlot();

      // Hide spine, reset, setup new animation — show on next frame
      this.winSpine.alpha = 0;
      this.winSpine.state.clearTracks();
      this.winSpine.state.clearListeners();
      this.winSpine.state.apply(this.winSpine.skeleton);
      this.winSpine.skeleton.setToSetupPose();

      // setToSetupPose restores ALL slot attachments to defaults.
      // When counter is detached, clear the value slot so the baked-in
      // attachment doesn't scale in with the bone during intermediate IN animations.
      if (this.counterDetached) {
        const valueSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
        if (valueSlot) valueSlot.setAttachment(null);
      }
      // Always clear tap slot — button visibility is managed manually
      const tapSlot = this.winSpine.skeleton.findSlot("txt_slot_tap");
      if (tapSlot) tapSlot.setAttachment(null);

      const animations = this.getAvailableAnimations();
      const { inAnimation, idleAnimation } = this.findAnimations(
        animations,
        winType,
      );

      if (isLastAnimation) {
        this.playFinalAnimation(inAnimation, idleAnimation, resolve, reject);
      } else {
        this.playIntermediateAnimation(
          inAnimation,
          idleAnimation,
          resolve,
          reject,
        );
      }
    });
  }

  /**
   * Start first animation in the sequence
   */
  startFirstAnimation() {
    return new Promise((resolve, reject) => {
      if (
        !this.winSpine ||
        !this.winSpine.state ||
        this.animationQueue.length === 0
      ) {
        resolve();
        return;
      }

      const winType = this.animationQueue[0]; // Start with first animation
      const isLastAnimation = this.animationQueue.length === 1;

      // Play main win sound once at animation start (based on final win type)
      this.playMainWinSound();

      // Play banner sound for first animation
      this.playBannerSound(winType);

      // Setup spine animation
      this.winSpine.state.clearTracks();
      this.winSpine.state.clearListeners();
      this.winSpine.state.apply(this.winSpine.skeleton);
      this.winSpine.skeleton.setToSetupPose();

      // Get animation names
      const animations = this.getAvailableAnimations();
      const { inAnimation, idleAnimation } = this.findAnimations(
        animations,
        winType,
      );

      // Calculate counter target and duration
      // MAX_WIN special case: count to totalWinAmount - 1 before max_win, then jump to final
      const isMaxWinFinal = this.finalWinType === "max_win" && !isLastAnimation;
      const counterTarget = isMaxWinFinal
        ? this.totalWinAmount - 1
        : this.totalWinAmount;
      const counterTiers = isMaxWinFinal
        ? this.animationQueue.length - 1 // Only count during pre-max tiers
        : this.animationQueue.length;
      const totalCounterDuration = counterTiers * SPINE_ANIMATION_DURATION;

      // Start continuous counter animation
      if (this.totalWinAmount > 0) {
        this.startCounterAnimation(
          0,
          counterTarget,
          totalCounterDuration,
          () => {
            // Counter finished naturally
            if (!isMaxWinFinal) {
              // Normal win: signal WinDisplay to show win text
              if (this.dispatch) {
                this.dispatch(setWinAnimationCounterFinished(true));
              }
              this.startAutoCloseTimer();
            }
            // Max win: counter finished at totalWinAmount-1, will be jumped in playFinalAnimation
          },
        );
      }

      // First animation always plays IN → IDLE (spine scales counter in via bone)
      if (inAnimation && idleAnimation) {
        // Play in animation for its actual duration
        this.winSpine.state.setAnimation(0, inAnimation, false);

        // Get actual duration for in animation from spine data
        const inDuration = this.getSpineAnimationDuration(`${winType}_in`);

        // After in animation completes, start looping idle animation
        const inTimeout = new TickerTimeout(() => {
          if (this.isPlaying) {
            this.winSpine.state.addAnimation(0, idleAnimation, true, 0);

            if (isLastAnimation) {
              // Single animation: wait for user click
              this.isWaitingForClick = true;
              this.currentAnimationPhase = "waiting-for-click";
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              resolve();
            } else {
              // Multi-animation: wait for tier duration then move to next
              const tierDuration = SPINE_ANIMATION_DURATION - inDuration;
              const idleTimeout = new TickerTimeout(
                () => {
                  if (this.isPlaying) {
                    this.currentAnimationIndex++;
                    this.phaseStartTime = performance.now();
                    this.phaseProgress = 0;
                    this.playNextAnimation().then(resolve).catch(reject);
                  }
                },
                Math.max(0, tierDuration),
              );
              this.currentTimeouts.push(idleTimeout);
            }
          }
        }, inDuration);

        this.currentTimeouts.push(inTimeout);
      } else if (idleAnimation) {
        // Fallback: just loop idle animation
        this.winSpine.state.setAnimation(0, idleAnimation, true);
        if (isLastAnimation) {
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
        }
        resolve();
      } else {
        resolve();
      }
    });
  }

  /**
   * Get animation duration from spine data with detailed extraction
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.winSpine ||
      !this.winSpine.skeleton ||
      !this.winSpine.skeleton.data ||
      !this.winSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      // Method 1: Try to get from skeleton data animations array
      const animation = this.winSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      // Method 2: Try to get from spine state data
      if (this.winSpine.state && this.winSpine.state.data) {
        const stateAnimation =
          this.winSpine.state.data.skeletonData.animations.find(
            (anim) => anim.name === animationName,
          );

        if (stateAnimation && stateAnimation.duration) {
          const durationMs = Math.round(stateAnimation.duration * 1000);
          return durationMs;
        }
      }

      // Method 3: Try to access the raw JSON data if available
      if (this.winSpine.skeleton.data.name && PIXI.Assets.cache) {
        // This might contain the original JSON data
        const skeletonAsset = Object.values(PIXI.Assets.cache._cache).find(
          (asset) =>
            asset && asset.animations && asset.animations[animationName],
        );

        if (skeletonAsset && skeletonAsset.animations[animationName]) {
          // Calculate duration from keyframes if available
          const animData = skeletonAsset.animations[animationName];
          let maxTime = 0;

          // Check all tracks for the longest duration
          Object.values(animData).forEach((track) => {
            if (Array.isArray(track)) {
              track.forEach((keyframe) => {
                if (keyframe.time && keyframe.time > maxTime) {
                  maxTime = keyframe.time;
                }
              });
            }
          });

          if (maxTime > 0) {
            const durationMs = Math.round(maxTime * 1000);
            return durationMs;
          }
        }
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Play intermediate animation — IDLE only, no IN/OUT
   * Counter keeps running continuously (started in startFirstAnimation)
   * Just swaps the spine visual to the new tier's idle animation
   */
  playIntermediateAnimation(inAnimation, idleAnimation, resolve, reject) {
    const winType = this.animationQueue[this.currentAnimationIndex];
    const tierDuration = SPINE_ANIMATION_DURATION;

    if (inAnimation && idleAnimation) {
      // Play IN animation (spine is alpha 0 from playNextAnimation)
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // Show on next frame — clears old visuals, new IN starts fresh
      requestAnimationFrame(() => {
        if (this.isPlaying) {
          this.winSpine.alpha = 1;
        }
      });

      // Get actual IN duration from spine data
      const inDuration = this.getSpineAnimationDuration(`${winType}_in`);

      // After IN completes, loop IDLE for remaining tier time
      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.winSpine.state.setAnimation(0, idleAnimation, true);

          // Wait remaining tier duration then move to next
          const remaining = Math.max(0, tierDuration - inDuration);
          const idleTimeout = new TickerTimeout(() => {
            if (this.isPlaying) {
              this.currentAnimationIndex++;
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              this.playNextAnimation().then(resolve).catch(reject);
            }
          }, remaining);
          this.currentTimeouts.push(idleTimeout);
        }
      }, inDuration);

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Fallback: just idle
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      requestAnimationFrame(() => {
        if (this.isPlaying) this.winSpine.alpha = 1;
      });

      const timeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation().then(resolve).catch(reject);
        }
      }, tierDuration);
      this.currentTimeouts.push(timeout);
    } else {
      this.currentAnimationIndex++;
      this.playNextAnimation().then(resolve).catch(reject);
    }
  }

  /**
   * Play final animation — jump to IDLE, wait for user click, OUT plays on close
   * Counter is already running continuously (started in startFirstAnimation)
   * No IN animation — counter stays visible at scale 1
   */
  playFinalAnimation(inAnimation, idleAnimation, resolve, reject) {
    const winType = this.animationQueue[this.currentAnimationIndex];

    // MAX WIN MUSIC SWITCH
    if (winType === "max_win") {
      try {
        soundManager.stop(SOUND_NAME.win_music);
        const maxWinMusic = HowlCache[SOUND_NAME.max_win_music];
        if (maxWinMusic) {
          maxWinMusic.loop(true);
          soundManager.fadeIn(SOUND_NAME.max_win_music, 500);
        }
      } catch (error) {
        // Silent fail
      }
    }

    // MAX WIN: jump counter from totalWinAmount-1 to totalWinAmount
    if (winType === "max_win") {
      if (!this.counterJumpedToFinal) {
        this.jumpCounterToValue(this.totalWinAmount);
        this.counterJumpedToFinal = true;
        if (this.dispatch) {
          this.dispatch(setWinAnimationCounterFinished(true));
        }
        this.startAutoCloseTimer();
      }
    }

    if (inAnimation && idleAnimation) {
      // Play IN animation (spine is alpha 0 from playNextAnimation)
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // Show on next frame
      requestAnimationFrame(() => {
        if (this.isPlaying) {
          this.winSpine.alpha = 1;
        }
      });

      // Get actual IN duration
      const inDuration = this.getSpineAnimationDuration(`${winType}_in`);

      // After IN completes, loop IDLE and wait for user click
      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          resolve();
        }
      }, inDuration);

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Fallback: just loop idle
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      requestAnimationFrame(() => {
        if (this.isPlaying) this.winSpine.alpha = 1;
      });
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      resolve();
    } else {
      this.finishAnimation();
      resolve();
    }
  }

  /**
   * Find appropriate animations from available list
   */
  findAnimations(animations, winType) {
    const inAnimation = animations.find((anim) => anim === `${winType}_in`);
    const idleAnimation = animations.find((anim) => anim === `${winType}_idle`);
    const outAnimation = animations.find((anim) => anim === `${winType}_out`);

    return { inAnimation, idleAnimation, outAnimation };
  }

  /**
   * Handle user click to skip counter or close animation
   *
   * Two-click logic:
   * 1. First click (during counter animation): Skips counter to final value
   * 2. Second click (after counter finished): Closes the entire animation
   *
   * Also handles spacebar input and prevents double-clicks with debouncing.
   *
   * @returns {boolean} True if click was handled, false if ignored
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.winSpine || !this.winSpine.state) {
      return false;
    }

    // Block interactions during closing or finishing phases
    if (this.isClosing || this.isFinishing) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
    // This ensures only ONE click is processed per 300ms window
    if (this.debounceTimeout) {
      return false;
    }

    // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks for 300ms
    // This timeout will be the ONLY timeout created, no matter how many clicks
    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Check if counter is currently animating
    const isCounterAnimating = this.isCounterAnimating();
    const currentCounterValue = this.getCurrentCounterValue();
    const isCounterFinished = currentCounterValue >= this.totalWinAmount;

    // SIMPLE LOGIC: If counter is animating, skip it; if finished, close animation
    if (isCounterAnimating && !isCounterFinished) {
      // Counter is still animating - skip to final
      this.hasHandledSkipClick = true;

      // Jump counter to final value immediately
      this.jumpCounterToValue(this.totalWinAmount);

      this.counterJumpedToFinal = true;

      // Counter skipped to final - signal WinDisplay to show win text
      if (this.dispatch) {
        this.dispatch(setWinAnimationCounterFinished(true));
      }

      // Skip to final animation
      this.skipToFinalAnimation();

      // Start auto-close timer AFTER skipToFinalAnimation (which clears all timeouts)
      this.startAutoCloseTimer();

      return true;
    } else {
      // Counter is finished or not animating - close animation
      this.hasHandledCloseClick = true;

      // Clear auto-close timer since user clicked manually
      if (this.autoCloseTimeout) {
        this.autoCloseTimeout.clear();
        this.autoCloseTimeout = null;
      }

      this.playClosingAnimation();

      return true;
    }
  }

  /**
   * Start auto-close timer for animations
   *
   * Automatically closes win animations if user doesn't interact after counter
   * reaches final value. Prevents animations from staying open indefinitely.
   * Timer is cleared if user clicks manually.
   */
  startAutoCloseTimer() {
    // Clear any existing auto-close timer
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
    }

    // Use MAX_WIN auto-close delay for max_win (30s), normal delay for other wins (3s)
    const autoCloseDelay =
      this.finalWinType === "max_win"
        ? FEATURES_CONFIG.MAX_WIN.AUTO_CLOSE_DELAY
        : FEATURES_CONFIG.WIN_CELEBRATION.AUTO_CLOSE_DELAY;

    // Start auto-close timer using configured delay
    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying && !this.hasHandledCloseClick) {
        this.hasHandledCloseClick = true;
        this.playClosingAnimation();
      }
    }, autoCloseDelay);
  }

  /**
   * Skip all intermediate animations and jump straight to final animation
   *
   * When user clicks skip during counter animation:
   * - Stops all current animations and timeouts
   * - Jumps counter to final value instantly
   * - Plays final win type animation (EPIC for highest wins)
   * - If already playing final animation, handles smooth transition
   *
   * Used for progressive wins to skip BIG→SUPER→MEGA and go straight to EPIC.
   */
  skipToFinalAnimation() {
    const finalWinType = this.animationQueue[this.animationQueue.length - 1];
    const isAlreadyAtFinalAnimation =
      this.currentAnimationIndex === this.animationQueue.length - 1;

    // If we're already playing the final animation, handle it smoothly
    if (isAlreadyAtFinalAnimation && this.isPlaying) {
      this.handleSkipDuringFinalAnimation(finalWinType);
      return;
    }

    // Clear all timeouts and animations to stop intermediate animations
    this.clearAllResources();

    // Stop current banner sounds only (keep win_music playing - it plays throughout entire sequence)
    this.stopBannerSounds();

    // MAX WIN MUSIC SWITCH: When skipping to max_win, switch from win_music to max_win_music
    if (finalWinType === "max_win") {
      try {
        soundManager.stop(SOUND_NAME.win_music);
        const maxWinMusic = HowlCache[SOUND_NAME.max_win_music];
        if (maxWinMusic) {
          maxWinMusic.loop(true);
          soundManager.fadeIn(SOUND_NAME.max_win_music, 500);
        }
      } catch (error) {
        // Silent fail
      }
    }

    // Jump to final animation index
    this.currentAnimationIndex = this.animationQueue.length - 1;

    // Jump counter to final value immediately when skipping (only if not already jumped)
    if (!this.counterJumpedToFinal) {
      this.jumpCounterToValue(this.totalWinAmount);
      this.counterJumpedToFinal = true;
      // Signal WinDisplay to show win text
      if (this.dispatch) {
        this.dispatch(setWinAnimationCounterFinished(true));
      }
    }

    // Show spine if we're coming from counter-only phase
    if (!this.isVisible) {
      this.winSpine.alpha = 1; // Show (use alpha not visible)
      this.isVisible = true;
    }

    // Play banner sound for the final win type when skipping
    this.playBannerSound(finalWinType);

    // Detach counter from slot so it stays visible during transition
    this.detachCounterFromSlot();

    // Hide → reset → new IN → show on next frame (same pattern as transitions)
    this.winSpine.alpha = 0;
    this.winSpine.state.clearTracks();
    this.winSpine.state.clearListeners();
    this.winSpine.state.apply(this.winSpine.skeleton);
    this.winSpine.skeleton.setToSetupPose();

    // setToSetupPose restores slot attachments — clear them again
    if (this.counterDetached) {
      const valueSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
      if (valueSlot) valueSlot.setAttachment(null);
    }
    const tapSlot = this.winSpine.skeleton.findSlot("txt_slot_tap");
    if (tapSlot) tapSlot.setAttachment(null);

    const animations = this.getAvailableAnimations();
    const { inAnimation, idleAnimation } = this.findAnimations(
      animations,
      finalWinType,
    );

    if (inAnimation && idleAnimation) {
      this.winSpine.state.setAnimation(0, inAnimation, false);

      // Show on next frame
      requestAnimationFrame(() => {
        if (this.isPlaying) {
          this.winSpine.alpha = 1;
          this.isVisible = true;
        }
      });

      // After IN completes, loop IDLE
      const inDuration = this.getSpineAnimationDuration(`${finalWinType}_in`);
      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
        }
      }, inDuration);
      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      requestAnimationFrame(() => {
        if (this.isPlaying) {
          this.winSpine.alpha = 1;
          this.isVisible = true;
        }
      });
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Handle skip during final animation
   * Checks current phase and transitions smoothly to idle/out
   */
  handleSkipDuringFinalAnimation(finalWinType) {
    const animations = this.getAvailableAnimations();
    const { idleAnimation } = this.findAnimations(animations, finalWinType);

    // Get current animation being played
    const currentAnimation = this.winSpine.state.tracks[0]?.animation?.name;

    // Check what phase we're currently in
    const inAnimationName = `${finalWinType}_in`;
    const idleAnimationName = `${finalWinType}_idle`;

    if (currentAnimation === inAnimationName) {
      // We're in the IN phase - smoothly transition to IDLE
      this.winSpine.state.clearListeners();

      if (idleAnimation) {
        // Use addAnimation for smooth blend from IN to IDLE
        this.winSpine.state.addAnimation(0, idleAnimation, true, 0);

        // Set waiting state
        this.isWaitingForClick = true;
        this.currentAnimationPhase = "waiting-for-click";
        this.phaseStartTime = performance.now();
        this.phaseProgress = 0;
      }
    } else if (currentAnimation === idleAnimationName) {
      // We're already in IDLE phase - if this was a skip action, stay in idle and wait for user
      // Only close if this wasn't triggered by a skip (i.e., natural progression)
      // Don't close - just ensure we're in waiting state
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    } else {
      // Fallback: assume we're in IDLE and stay there (don't close)
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.winSpine?.state) return;

    this.isClosing = true;
    this.isWaitingForClick = false;
    this.hideClickIndicator();
    this.hideButton();

    // Handle music transition based on win type
    try {
      if (this.finalWinType === "max_win") {
        // Max win: stop max_win_music and play max_win_out sound
        soundManager.stop(SOUND_NAME.max_win_music);
        soundManager.play(SOUND_NAME.max_win_out);
      } else {
        // Normal win: stop win_music and play win_music_end
        soundManager.stop(SOUND_NAME.win_music);
        soundManager.play(SOUND_NAME.win_music_end);
      }
    } catch (error) {
      // Failed to handle music transition
    }

    // DON'T hide counter here - let it follow the spine's OUT animation
    // Reattach counter to slot so it scales out with the bone (1→0)
    this.reattachCounterToSlot();

    const animations = this.getAvailableAnimations();
    const { outAnimation } = this.findAnimations(animations, this.finalWinType);
    const outAnim = outAnimation || animations[0];

    this.winSpine.state.clearListeners();

    if (outAnim) {
      this.winSpine.state.setAnimation(0, outAnim, false);
      this.winSpine.state.addListener({
        complete: (e) => {
          if (e.animation.name === outAnim && this.isPlaying) {
            this.finishAnimation();
          }
        },
      });
    } else {
      this.finishAnimation();
    }
  }

  /**
   * Complete the animation sequence and perform full cleanup
   *
   * Called when user closes animation or auto-close timer expires.
   * Performs final cleanup: stops sounds, hides UI, clears resources,
   * dispatches Redux state changes, and resets all animation state.
   * This is the final method called when win celebration ends.
   */
  finishAnimation() {
    if (!this.winSpine || !this.initialized) {
      return;
    }

    // Set finishing flag immediately to prevent retriggering
    this.isFinishing = true;

    this.stopBoneUpdateLoop();

    // Dispatch Redux state change immediately before any async operations
    if (this.dispatch) {
      this.dispatch(setWinAnimationsPlaying(false));
      this.dispatch(setWinAnimationCounterFinished(false));
      // Unblock symbol popup when win animations finish
      this.dispatch(setSymbolPopupBlocked(false));

      // Skip balance update when this is an H&W trigger round — the coin animations
      // are about to play and a balance update would cause jitter.
      // SceneTransitionOverlay already handles balance when returning to base game.
      const featureState = store.getState()?.game?.featureState;
      const isHoldAndWinTrigger =
        featureState?.type === "holdAndWin" && featureState?.phase === "intro";

      // Skip balance update during free spins — balance is synced once at the end of the feature
      const isFreeSpinActive = store.getState()?.featureGame?.freeSpinActive;

      // Provider-specific balance update strategy:
      // PopiPlay: Call refreshGameData() to sync with server (server tracks all transactions)
      // Redstone: Call updateDisplayedBalance() to sync displayed balance with Redux balance (already correct from spin)
      if (!isHoldAndWinTrigger && !isFreeSpinActive) {
        if (handleShouldSyncBalanceOnWinClose()) {
          this.dispatch(refreshGameData());
        } else {
          this.dispatch(updateDisplayedBalance());
        }
      }
    }

    // Stop win_music and max_win_music when animation finishes (safety stop — should already be stopped in playClosingAnimation)
    try {
      if (SOUND_NAME.win_music) {
        soundManager.stop(SOUND_NAME.win_music);
      }
      if (SOUND_NAME.max_win_music) {
        soundManager.stop(SOUND_NAME.max_win_music);
      }
    } catch (error) {
      // Silent fail
    }

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    // Clear all resources to prevent buildup
    this.clearAllResources();

    // Reset playing states
    this.isPlaying = false;
    this.isWaitingForClick = false;

    // Disable scaling calculations when animation finishes
    this.shouldBeActive = false;

    // Reset phase tracking
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Counter doesn't need manual hiding - it's a child of spine and will hide with it
    // The spine's alpha change will automatically hide the counter

    // Hide click indicator instantly (cleanup)
    this.hideClickIndicatorInstant();

    // Hide spine immediately when finishAnimation starts to prevent visibility issues
    if (this.winSpine) {
      this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
    }
    this.isVisible = false;

    // Fade out background with callback to clean up state after animation completes
    this.overlayRef.current?.fadeOutBackground(() => {
      // Stop banner sounds when overlay fades out completely
      this.stopBannerSounds();

      // Double-check spine is still hidden (defensive programming)
      if (this.winSpine) {
        this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
      }
      this.isVisible = false;
    });

    // Reset animation queue and stored values
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.totalWinAmount = null;
    this.betAmount = null;
    this.lastWinAmount = null; // Reset to allow new win animations
    this.counterJumpedToFinal = false;
    this.counterDetached = false;

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset spine timeScale in case it was paused
    if (this.winSpine && this.winSpine.state) {
      this.winSpine.state.timeScale = 1;
    }

    // Reset auto-close timeout
    this.autoCloseTimeout = null;

    // Reset finishing flag after all cleanup is complete
    this.isFinishing = false;
  }

  /**
   * Immediately stop all win animations and perform emergency cleanup
   *
   * Used when component unmounts or critical errors occur.
   * Unlike finishAnimation(), this doesn't dispatch Redux state changes
   * or perform full UI transitions - just stops everything immediately.
   */
  stopWinAnimation() {
    this.stopBoneUpdateLoop();

    // Clear all resources (timeouts + animations)
    this.clearAllResources();

    // Stop all win sounds
    this.stopAllWinSounds();

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    if (this.winSpine && this.isPlaying) {
      this.winSpine.state?.clearListeners();
      this.winSpine.state?.clearTracks();
      this.winSpine.alpha = 0.001; // Hide (use alpha not visible)
      this.isPlaying = false;
      this.isVisible = false;

      // Disable scaling calculations when animation is stopped
      this.shouldBeActive = false;

      // Hide click indicator instantly (cleanup)
      this.hideClickIndicatorInstant();

      this.overlayRef.current?.fadeOutBackground();
    }

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset phase tracking
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Reset finishing flag if animation was stopped
    this.isFinishing = false;
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stopBoneUpdateLoop();

    // Clear all resources before cleanup
    this.clearAllResources();

    // Cleanup counter
    if (this.counterContainer) {
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
      this.counterText = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    // Disable scaling calculations when animation is no longer active
    this.shouldBeActive = false;

    if (this.winSpine) {
      // Defensive cleanup - ensure spine state is properly cleared
      if (this.winSpine.state) {
        try {
          this.winSpine.state.clearListeners();
          this.winSpine.state.clearTracks();
          this.winSpine.state.timeScale = 1.0;
        } catch (error) {
          // Spine might already be destroyed - continue cleanup
        }
      }

      // Remove from container safely
      if (this.container && this.container.children.includes(this.winSpine)) {
        this.container.removeChild(this.winSpine);
      }

      // Destroy spine instance
      try {
        this.winSpine.destroy();
      } catch (error) {
        // Spine might already be destroyed - continue cleanup
      }

      this.winSpine = null;
    }

    this.initialized = false;
  }
}

export default WinAnimationController;
